import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateProfile, fetchProfile } from '../../features/auth/authSlice';

const BELT_RANKS = [
  'White',
  'Blue',
  'Purple',
  'Brown',
  'Black',
];

const EditProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, loading, error, isAuthenticated } = useSelector((state) => state.auth);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    belt_rank: '',
    academy: '',
    start_date: '',
  });

  const [errorMessage, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!user) {
      dispatch(fetchProfile());
    } else {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        belt_rank: user.belt_rank || '',
        academy: user.academy || '',
        start_date: user.start_date || '',
      });
    }
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Submitting form data:', formData);
      const result = await dispatch(updateProfile(formData)).unwrap();
      console.log('Update response:', result);
      await dispatch(fetchProfile()).unwrap();
      navigate('/profile');
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(err.message || 'Failed to update profile');
    }
  };

  if (loading || !user) {
    return (
      <Container sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography component="h1" variant="h4" align="center" gutterBottom>
          Edit Profile
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                disabled
                helperText="Username cannot be changed"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!formData.email}
                helperText={!formData.email ? "Please enter your email" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Belt Rank"
                name="belt_rank"
                value={formData.belt_rank}
                onChange={handleChange}
                error={!formData.belt_rank}
                helperText={!formData.belt_rank ? "Please select your belt rank" : ""}
              >
                {BELT_RANKS.map((rank) => (
                  <MenuItem key={rank} value={rank}>
                    {rank}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Academy"
                name="academy"
                value={formData.academy}
                onChange={handleChange}
                error={!formData.academy}
                helperText={!formData.academy ? "Please enter your academy" : ""}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Start Date"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                error={!formData.start_date}
                helperText={!formData.start_date ? "Please select your start date" : ""}
              />
            </Grid>
          </Grid>

          <Box sx={{ 
            mt: 4, 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'center',
            flexWrap: 'wrap' 
          }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || !formData.belt_rank || !formData.academy || !formData.start_date}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/profile')}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default EditProfile;